import { useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { Check, GripVertical, Maximize2, Minimize2, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TestCaseStatusBadge } from '@/components/projects/TestCaseStatusBadge'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { useAllUsers, useTestPlans, useUpdateTestCase } from '@/hooks'
import { cn, getInitials } from '@/lib/utils'
import { TestCaseEditableStatuses } from '@/types'
import type { LinkedWorkItem, TestCaseDetail, UpdateTestCaseRequest, WorkItem } from '@/types'
import { AddWorkItemDialog } from './AddWorkItemDialog'
import { DiscardEditConfirmDialog } from './DiscardEditConfirmDialog'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessages } from '@/lib/validationErrors'

const TITLE_MAX = 256
const DESCRIPTION_MAX = 50000
const STEP_MAX = 2000

interface EditTestCaseDialogProps {
  projectId: string
  testCase: TestCaseDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface EditTestCaseFormValues {
  title: string
  status: string
  testSuiteId: string
  description: string
  assigneeUserId: string | null
  linkedWorkItems: LinkedWorkItem[]
  steps: { description: string; expectedResult: string }[]
}

function buildDefaultValues(testCase: TestCaseDetail): EditTestCaseFormValues {
  return {
    title: testCase.title,
    status: TestCaseEditableStatuses.includes(testCase.status as typeof TestCaseEditableStatuses[number])
      ? testCase.status
      : '',
    testSuiteId: testCase.testSuiteId,
    description: testCase.description ?? '',
    assigneeUserId: testCase.assigneeUserId ?? null,
    linkedWorkItems: [...testCase.linkedWorkItems],
    steps: testCase.steps.map((s) => ({ description: s.description, expectedResult: s.expectedResult ?? '' })),
  }
}

export function EditTestCaseDialog({ projectId, testCase, open, onOpenChange }: EditTestCaseDialogProps) {
  const updateTestCase = useUpdateTestCase(projectId)
  const { data: allUsers = [] } = useAllUsers()
  const users = useMemo(() => allUsers.filter((u) => u.status === 'Active'), [allUsers])
  const { data: testPlansData } = useTestPlans(projectId, { maxResultCount: 100, skipCount: 0 })
  const testSuites = useMemo(
    () => (testPlansData?.items ?? []).flatMap((p) => p.testSuites),
    [testPlansData],
  )
  const [fullScreen, setFullScreen] = useState(false)
  const [addWorkItemOpen, setAddWorkItemOpen] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [draggedStepIndex, setDraggedStepIndex] = useState<number | null>(null)

  const { register, handleSubmit, watch, reset, control, setValue, formState: { errors, isDirty } } =
    useForm<EditTestCaseFormValues>({
      defaultValues: buildDefaultValues(testCase),
    })

  const { fields, append, remove, move } = useFieldArray({ control, name: 'steps' })

  useEffect(() => {
    if (open) {
      reset(buildDefaultValues(testCase))
      setFullScreen(false)
    }
  }, [open, testCase, reset])

  const titleValue = watch('title') ?? ''
  const linkedWorkItems = watch('linkedWorkItems')
  const assigneeUserId = watch('assigneeUserId')

  const selectedAssignee = useMemo<{ displayName: string; email: string } | undefined>(() => {
    if (!assigneeUserId) return undefined

    const known = users.find((u) => u.id === assigneeUserId)
    if (known) return { displayName: known.displayName, email: known.email }

    return { displayName: testCase.assigneeName ?? '', email: '' }
  }, [users, assigneeUserId, testCase.assigneeName])

  const onSubmit = async (data: EditTestCaseFormValues) => {
    const payload: UpdateTestCaseRequest = {
      title: data.title.trim(),
      status: data.status,
      testSuiteId: data.testSuiteId,
      description: data.description.trim() === '' ? null : data.description.trim(),
      linkedWorkItemIds: data.linkedWorkItems.map((w) => w.id),
      steps: data.steps
        .filter((s) => s.description.trim().length > 0)
        .map((s) => ({
          description: s.description.trim(),
          expectedResult: s.expectedResult.trim() === '' ? null : s.expectedResult.trim(),
        })),
    }
    try {
      await updateTestCase.mutateAsync({ id: testCase.id, data: payload })
      toast.success('Test case updated successfully')
      onOpenChange(false)
    } catch (error) {
      const validationMessages = extractValidationMessages(error)
      toast.error(
        validationMessages.length > 0
          ? validationMessages.join(' ')
          : getErrorMessage(error) ?? 'Failed to update test case',
      )
    }
  }

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && isDirty) {
      setDiscardConfirmOpen(true)
      return
    }
    onOpenChange(nextOpen)
  }

  const handleDiscard = () => {
    setDiscardConfirmOpen(false)
    reset(buildDefaultValues(testCase))
    onOpenChange(false)
  }

  const handleWorkItemResolved = (workItem: WorkItem) => {
    const current = watch('linkedWorkItems')
    if (current.some((w) => w.id === workItem.id)) {
      toast.error('This work item is already linked')
      return
    }
    const next: LinkedWorkItem = {
      id: workItem.id,
      number: workItem.number,
      title: workItem.title,
      epic: workItem.epic,
      status: workItem.status,
    }
    setValue('linkedWorkItems', [...current, next], { shouldDirty: true })
    toast.success(`Work item successfully linked to ${testCase.testCaseIdentifier}`)
  }

  const handleRemoveLinkedWorkItem = (workItemId: string) => {
    const current = watch('linkedWorkItems')
    setValue('linkedWorkItems', current.filter((w) => w.id !== workItemId), { shouldDirty: true })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          hideCloseButton
          className={cn(
            'fixed top-0 right-0 left-auto translate-x-0 translate-y-0 h-full max-h-screen p-0 gap-0 border-l rounded-none flex flex-col bg-background',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            fullScreen
              ? 'w-screen max-w-none'
              : 'w-full sm:max-w-[800px] sm:w-[800px]',
          )}
        >
          <DialogTitle className="sr-only">
            Edit {testCase.testCaseIdentifier}: {testCase.title}
          </DialogTitle>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full min-h-0">
            <div className="px-6 py-4 border-b flex items-center gap-3 shrink-0">
              <button
                type="button"
                className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
                onClick={() => handleClose(false)}
                aria-label="Close edit test case panel"
                data-testid="edit-test-case-close"
              >
                <X className="h-5 w-5" />
              </button>
              <h2
                className="flex-1 text-lg font-bold truncate"
                title={`${testCase.testCaseIdentifier}: ${titleValue || testCase.title}`}
                data-testid="edit-test-case-heading"
              >
                {testCase.testCaseIdentifier}: {titleValue || testCase.title}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => setFullScreen((v) => !v)}
                aria-label={fullScreen ? 'Collapse panel' : 'Expand panel to full screen'}
              >
                {fullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                type="submit"
                disabled={updateTestCase.isPending}
                data-testid="edit-test-case-save-button"
                className="gap-1.5"
              >
                <Check className="h-4 w-4" />
                {updateTestCase.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="edit-test-case-title">
                  Test Case Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-test-case-title"
                  placeholder="Enter test case title"
                  data-testid="edit-test-case-title-input"
                  {...register('title', {
                    validate: (value) => {
                      const trimmed = value.trim()
                      if (trimmed.length === 0) return 'Title is required'
                      if (trimmed.length > TITLE_MAX) return `Title must be at most ${TITLE_MAX} characters`
                      return true
                    },
                  })}
                />
                {errors.title && (
                  <p role="alert" className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-test-case-test-suite">
                    Test Suite <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="testSuiteId"
                    control={control}
                    rules={{ required: 'Test Suite is required' }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="edit-test-case-test-suite" data-testid="edit-test-case-test-suite-trigger">
                          <SelectValue placeholder="Select a test suite" />
                        </SelectTrigger>
                        <SelectContent>
                          {testSuites.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">No test suites available.</div>
                          ) : (
                            testSuites.map((suite) => (
                              <SelectItem key={suite.id} value={suite.id}>
                                {suite.testSuiteIdentifier}: {suite.title}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.testSuiteId && (
                    <p role="alert" className="text-sm text-destructive">{errors.testSuiteId.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-test-case-status">
                    Status <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: 'Status is required' }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="edit-test-case-status" data-testid="edit-test-case-status-trigger">
                          {field.value ? (
                            <TestCaseStatusBadge status={field.value} />
                          ) : (
                            <SelectValue placeholder="Select a status" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {TestCaseEditableStatuses.map((status) => (
                            <SelectItem key={status} value={status}>
                              <TestCaseStatusBadge status={status} />
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.status && (
                    <p role="alert" className="text-sm text-destructive">{errors.status.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium leading-none">Assignee</span>
                  {selectedAssignee ? (
                    <div
                      role="group"
                      aria-label="Assignee"
                      className="flex h-10 items-center gap-2 min-w-0"
                      data-testid="edit-test-case-assignee-display"
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="bg-primary text-primary-foreground text-[10px] font-semibold">
                          {getInitials(selectedAssignee.displayName || selectedAssignee.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{selectedAssignee.displayName}</span>
                    </div>
                  ) : (
                    <span
                      role="group"
                      aria-label="Assignee"
                      className="flex h-10 items-center text-sm text-muted-foreground"
                      data-testid="edit-test-case-assignee-display"
                    >
                      Unassigned
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Linked Work Items</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAddWorkItemOpen(true)}
                    data-testid="edit-test-case-add-work-item-button"
                    className="gap-1 h-7"
                  >
                    <Plus className="h-4 w-4" />
                    New Work Items
                  </Button>
                </div>
                {linkedWorkItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="edit-test-case-no-linked-work-item">
                    No linked work items.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {linkedWorkItems.map((wi) => (
                      <Card
                        key={wi.id}
                        className="flex items-center gap-3 p-3 shadow-none"
                        data-testid={`edit-test-case-linked-work-item-${wi.id}`}
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted shrink-0">
                          <span className="text-xs font-bold text-muted-foreground">WI</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {wi.number !== null && wi.number !== undefined
                              ? `WI-${String(wi.number).padStart(3, '0')}: `
                              : ''}
                            {wi.title}
                          </p>
                        </div>
                        <WorkItemStatusBadge status={wi.status} />
                        <Button
                          type="button"
                          variant="ghost"
                          size="iconSm"
                          onClick={() => handleRemoveLinkedWorkItem(wi.id)}
                          aria-label={`Remove linked work item ${wi.title}`}
                          data-testid={`edit-test-case-remove-linked-work-item-${wi.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-test-case-description" className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea
                  id="edit-test-case-description"
                  placeholder="Describe the test case"
                  rows={5}
                  data-testid="edit-test-case-description-input"
                  {...register('description', {
                    validate: (value) => {
                      if (value && value.trim().length > DESCRIPTION_MAX) {
                        return `Description must be at most ${DESCRIPTION_MAX} characters`
                      }
                      return true
                    },
                  })}
                />
                {errors.description && (
                  <p role="alert" className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Test Steps</Label>
                {fields.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="edit-test-case-no-steps">
                    No test steps. Use &quot;Add step&quot; to create one.
                  </p>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <Table aria-label="Test steps">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">#</TableHead>
                          <TableHead>Step Name</TableHead>
                          <TableHead>Expected Result</TableHead>
                          <TableHead className="w-[64px]" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fields.map((field, idx) => (
                          <TableRow
                            key={field.id}
                            className={cn('align-top', draggedStepIndex === idx && 'opacity-50')}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault()
                              if (draggedStepIndex !== null && draggedStepIndex !== idx) {
                                move(draggedStepIndex, idx)
                              }
                              setDraggedStepIndex(null)
                            }}
                          >
                            <TableCell className="text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <span
                                  draggable
                                  onDragStart={() => setDraggedStepIndex(idx)}
                                  onDragEnd={() => setDraggedStepIndex(null)}
                                  aria-label={`Drag to reorder step ${idx + 1}`}
                                  data-testid={`edit-test-case-step-drag-handle-${idx + 1}`}
                                  className="cursor-grab text-muted-foreground/70 hover:text-muted-foreground active:cursor-grabbing"
                                >
                                  <GripVertical className="h-4 w-4" />
                                </span>
                                {idx + 1}
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-normal">
                              <Input
                                placeholder="Step description"
                                aria-label={`Step ${idx + 1} name`}
                                data-testid={`edit-test-case-step-input-${idx + 1}`}
                                className="h-auto border-0 bg-transparent shadow-none px-0 py-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                {...register(`steps.${idx}.description`, {
                                  validate: (value) => {
                                    const trimmed = value.trim()
                                    if (trimmed.length === 0) return 'Step description is required'
                                    if (trimmed.length > STEP_MAX) return `Step must be at most ${STEP_MAX} characters`
                                    return true
                                  },
                                })}
                              />
                              {errors.steps?.[idx]?.description && (
                                <p role="alert" className="text-sm text-destructive">
                                  {errors.steps[idx]?.description?.message}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-normal">
                              <Input
                                placeholder="Expected result"
                                aria-label={`Step ${idx + 1} expected result`}
                                data-testid={`edit-test-case-step-expected-input-${idx + 1}`}
                                className="h-auto border-0 bg-transparent shadow-none px-0 py-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                {...register(`steps.${idx}.expectedResult`, {
                                  validate: (value) => {
                                    if (value.trim().length > STEP_MAX) return `Expected result must be at most ${STEP_MAX} characters`
                                    return true
                                  },
                                })}
                              />
                              {errors.steps?.[idx]?.expectedResult && (
                                <p role="alert" className="text-sm text-destructive">
                                  {errors.steps[idx]?.expectedResult?.message}
                                </p>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="iconSm"
                                onClick={() => remove(idx)}
                                aria-label={`Remove step ${idx + 1}`}
                                data-testid={`edit-test-case-remove-step-${idx + 1}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ description: '', expectedResult: '' })}
                  data-testid="edit-test-case-add-step-button"
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add step
                </Button>
              </div>
            </div>

          </form>
        </DialogContent>
      </Dialog>

      <AddWorkItemDialog
        projectId={projectId}
        open={addWorkItemOpen}
        onOpenChange={setAddWorkItemOpen}
        onResolved={handleWorkItemResolved}
      />

      <DiscardEditConfirmDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onConfirm={handleDiscard}
      />
    </>
  )
}
